import { useMemo, useState } from 'react'
import { Field, HStack, NativeSelect, Spinner, Text, VStack } from '@chakra-ui/react'
import { PageContainer, Card } from '../components/shared'
import { AdminGrantTable } from '../components/admin/grants/AdminGrantTable'
import { useAdminCanvasserGrantList, useAdminAttendeeGrantList } from '../hooks/useAdminGrants'
import { useCampaigns } from '../hooks/useCampaign'
import { useLobbyDayEvents } from '../hooks/useLobbyDay'
import { extractApiMessage } from '../lib/apiError'
import type { CanvasserGrantStatus } from '../types/canvasserGrant'
import type { AdminGrantDisplayRow } from '../types/adminGrant'

// L11 slice 3b -> LW-24 Slice 3 Task 4: the committee-wide Admin grant surface, now two grids
// (canvasser + attendee) sharing the kind-parameterized AdminGrantTable (Task 3) over the
// kind-aware hooks (Task 2) and the kind-segmented /api/admin/grants controller (Task 1). The
// campaign/event filter is SERVER-side (it is also the cap's narrowing lever -- each endpoint
// 400s with "narrow with campaignId/eventId" when a committee tree holds more grants than the
// list may materialize); the status filter is CLIENT-side over the already-capped page, so
// changing it never refetches.
const STATUSES: CanvasserGrantStatus[] = ['Active', 'Expired', 'Revoked', 'Blocked']

function CanvasserGrantsSection() {
  const [campaignId, setCampaignId] = useState('')
  const [status, setStatus] = useState<'' | CanvasserGrantStatus>('')

  const { data: campaigns, isLoading: campaignsLoading } = useCampaigns()
  const { data: grants, isLoading, error } = useAdminCanvasserGrantList(campaignId || undefined)

  const rows = useMemo<AdminGrantDisplayRow[]>(
    () => (grants ?? [])
      .filter((g) => !status || g.status === status)
      .map((g) => ({
        grantId: g.grantId,
        contactId: g.contactId,
        fullName: g.fullName,
        groupName: g.campaignName,
        portraitUrl: g.portraitUrl ?? null,
        status: g.status,
        activeSessionCount: g.activeSessionCount,
        lastSeenAt: g.lastSeenAt,
      })),
    [grants, status],
  )

  return (
    <VStack gap={4} align="stretch">
      <Text fontSize="lg" fontWeight="semibold">Canvasser Grants</Text>

      <Card>
        <HStack gap={4} flexWrap="wrap" align="end">
          <Field.Root disabled={campaignsLoading} maxW="320px">
            <Field.Label htmlFor="admin-grant-campaign">Campaign</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                id="admin-grant-campaign"
                placeholder="All campaigns"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
              >
                {(campaigns ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root maxW="240px">
            <Field.Label htmlFor="admin-grant-status">Status</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                id="admin-grant-status"
                placeholder="All statuses"
                value={status}
                onChange={(e) => setStatus(e.target.value as '' | CanvasserGrantStatus)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </HStack>
      </Card>

      {isLoading && (
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="muted">Loading canvasser grants...</Text>
        </VStack>
      )}

      {!!error && (
        <Card borderColor="danger.fg">
          <Text color="danger.fg">
            {extractApiMessage(error, 'Failed to load canvasser grants. Please try again.')}
          </Text>
        </Card>
      )}

      {!isLoading && !error && <AdminGrantTable kind="canvasser" rows={rows} />}
    </VStack>
  )
}

function AttendeeGrantsSection() {
  const [eventId, setEventId] = useState('')
  const [status, setStatus] = useState<'' | CanvasserGrantStatus>('')

  const { data: events, isLoading: eventsLoading } = useLobbyDayEvents()
  const { data: grants, isLoading, error } = useAdminAttendeeGrantList(eventId || undefined)

  const rows = useMemo<AdminGrantDisplayRow[]>(
    () => (grants ?? [])
      .filter((g) => !status || g.status === status)
      .map((g) => ({
        grantId: g.grantId,
        contactId: g.contactId,
        fullName: g.fullName,
        groupName: g.eventName,
        status: g.status,
        activeSessionCount: g.activeSessionCount,
        lastSeenAt: g.lastSeenAt,
      })),
    [grants, status],
  )

  return (
    <VStack gap={4} align="stretch">
      <Text fontSize="lg" fontWeight="semibold">Attendee Grants</Text>

      <Card>
        <HStack gap={4} flexWrap="wrap" align="end">
          <Field.Root disabled={eventsLoading} maxW="320px">
            <Field.Label htmlFor="admin-grant-event">Event</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                id="admin-grant-event"
                placeholder="All events"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              >
                {(events ?? []).map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.name}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root maxW="240px">
            <Field.Label htmlFor="admin-grant-attendee-status">Status</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                id="admin-grant-attendee-status"
                placeholder="All statuses"
                value={status}
                onChange={(e) => setStatus(e.target.value as '' | CanvasserGrantStatus)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </HStack>
      </Card>

      {isLoading && (
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="muted">Loading attendee grants...</Text>
        </VStack>
      )}

      {!!error && (
        <Card borderColor="danger.fg">
          <Text color="danger.fg">
            {extractApiMessage(error, 'Failed to load attendee grants. Please try again.')}
          </Text>
        </Card>
      )}

      {!isLoading && !error && <AdminGrantTable kind="attendee" rows={rows} />}
    </VStack>
  )
}

export function AdminGrantsPage() {
  return (
    <PageContainer
      title="Access Grants"
      subtitle="Canvasser and attendee field access across this committee — block a person, remove a grant, or sign a device out"
    >
      <VStack gap={8} align="stretch">
        <CanvasserGrantsSection />
        <AttendeeGrantsSection />
      </VStack>
    </PageContainer>
  )
}
